/**
 * Cart Buy All Logic
 * Syncs `_isBuyAll` on line items when `_buyAllApplicableProductCodes` is set,
 * after cart contents change.
 *
 * Triggers:
 * - `cart:update` / `cart:updated` — fire-and-forget sync only (no section re-fetch;
 *   use `fetch` interception or cart-items inline await for a correct first paint)
 * - Successful `fetch` to `/cart/add` — awaits sync before returning the response
 *   so add handlers render the minicart once with correct properties
 */

/**
 * SSR cart size from layout/theme.liquid. Null if the attribute is missing (fallback to /cart.js).
 * @returns {number | null}
 */
function getRenderedCartItemCount() {
    const raw = document.documentElement?.getAttribute('data-cart-item-count');
    if (raw == null || raw === '') return null;
    const count = Number.parseInt(raw, 10);
    return Number.isFinite(count) ? count : null;
}

window.syncBuyAllPropertiesAfterCartChange = async function () {
    try {
        const root = window.Shopify?.routes?.root || '/';
        const response = await fetch(`${root}cart.js`, {
            credentials: 'same-origin',
            headers: { Accept: 'application/json' },
        });
        if (!response.ok) return false;

        const cart = await response.json();
        if (!cart || !cart.items) return false;

        const cartSkus = cart.items.map(item => item.sku).filter(Boolean);
        const cartProductIds = cart.items.map(item => item.product_id?.toString()).filter(Boolean);
        const cartVariantIds = cart.items.map(item => item.variant_id?.toString()).filter(Boolean);

        let updatesNeeded = false;
        const changeRequests = [];

        cart.items.forEach((item, index) => {
            const line = index + 1;
            if (item.properties && item.properties['_buyAllApplicableProductCodes']) {
                const codesStr = item.properties['_buyAllApplicableProductCodes'];
                const codes = codesStr.split(',').map(c => c.trim()).filter(Boolean);
                if (codes.length === 0) return;

                const allPresent = codes.every(code =>
                    cartSkus.includes(code) ||
                    cartProductIds.includes(code) ||
                    cartVariantIds.includes(code)
                );

                const currentIsBuyAllValue = item.properties['_isBuyAll'];
                const currentIsBuyAll = currentIsBuyAllValue === 'true' || currentIsBuyAllValue === true || currentIsBuyAllValue === 'True';

                if (allPresent && !currentIsBuyAll) {
                    updatesNeeded = true;
                    changeRequests.push({
                        line: line,
                        quantity: item.quantity,
                        properties: {
                            ...item.properties,
                            '_isBuyAll': 'true'
                        }
                    });
                } else if (!allPresent && currentIsBuyAll) {
                    updatesNeeded = true;
                    changeRequests.push({
                        line: line,
                        quantity: item.quantity,
                        properties: {
                            ...item.properties,
                            '_isBuyAll': 'false'
                        }
                    });
                }
            }
        });

        if (updatesNeeded && changeRequests.length > 0) {
            for (const req of changeRequests) {
                const res = await fetch(`${window.Shopify?.routes?.root || '/'}cart/change.js`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(req)
                });
                if (!res.ok) {
                    console.error('CartBuyAllLogic Error posting to change.js:', await res.text(), req);
                }
            }
            return true;
        }
    } catch (e) {
        // A dropped connection ("Failed to fetch") is not a bug here; the next cart change syncs again.
        const log = e instanceof TypeError ? console.warn : console.error;
        log('Error in window.syncBuyAllPropertiesAfterCartChange:', e);
    }
    return false;
};

/** Coalesce document events in the same turn into one microtask (sync only, no UI). */
let buyAllSyncMicrotaskPending = false;

function requestBuyAllSyncAfterCartActivity() {
    if (buyAllSyncMicrotaskPending) return;
    buyAllSyncMicrotaskPending = true;
    queueMicrotask(async () => {
        buyAllSyncMicrotaskPending = false;
        try {
            await window.syncBuyAllPropertiesAfterCartChange();
        } catch (_) {
            // Errors already logged inside sync
        }
    });
}

/**
 * @param {RequestInfo|URL|string} input
 * @returns {string}
 */
function resolveFetchUrl(input) {
    if (typeof input === 'string') return input;
    if (typeof URL !== 'undefined' && input instanceof URL) return input.href;
    if (typeof Request !== 'undefined' && input instanceof Request) return input.url;
    if (input && typeof input === 'object' && 'url' in input) return String(/** @type {{ url: string }} */ (input).url);
    return '';
}

function setupBuyAllCartActivityHooks() {
    document.addEventListener('cart:update', requestBuyAllSyncAfterCartActivity);
    // Collection / legacy scripts (see main-collection.liquid, cart-page-products.liquid)
    document.addEventListener('cart:updated', requestBuyAllSyncAfterCartActivity);

    if (typeof window.fetch !== 'function') return;

    const originalFetch = window.fetch.bind(window);
    window.fetch = async function (input, init) {
        const response = await originalFetch(input, init);
        try {
            const url = resolveFetchUrl(input);
            // Run buy-all sync after the add response is returned so PDP add-to-cart is not
            // blocked on cart.js + cart/change.js. If properties change, cart-items re-renders.
            if (url.includes('/cart/add') && response.ok && window.syncBuyAllPropertiesAfterCartChange) {
                void window.syncBuyAllPropertiesAfterCartChange().then((changed) => {
                    if (changed) {
                        document.dispatchEvent(new CustomEvent('cart:buy-all-synced', { bubbles: true }));
                    }
                });
            }
        } catch (_) {
            // Never break callers of fetch
        }
        return response;
    };
}

document.addEventListener('DOMContentLoaded', () => {
    // IW-AUC-09: empty bag already known from Liquid — do not GET /cart.js on every page.
    if (getRenderedCartItemCount() === 0) return;
    void window.syncBuyAllPropertiesAfterCartChange();
});

setupBuyAllCartActivityHooks();
